/**
 * Oudh Al Anfar — cart behaviours
 * ---------------------------------------------------------------------------
 * Three commercial mechanics from the approved cart rules, implemented against
 * Shopify's standard cart events rather than Horizon internals, so a future
 * Horizon update cannot silently break them:
 *
 *   <oaa-shipping-meter>  progress toward the free-delivery threshold
 *   <oaa-sampler-strip>   discovery samples, capped at three then removed
 *   <oaa-cart-undo>       six seconds to undo a line removal
 *
 * Implementation note on undo. The approved rule reads "an undo toast holds the
 * line item before the removal commits". Intercepting Horizon's own remove
 * control would mean reaching into its internals, so this instead watches the
 * cart for a line that disappeared and offers to restore it — identical from
 * the shopper's side, and decoupled from the theme's cart implementation.
 * ---------------------------------------------------------------------------
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const CART_URL = window.Theme?.routes?.cart_url || '/cart';
const CART_ADD_URL = window.Theme?.routes?.cart_add_url || '/cart/add.js';

/** Snapshot of the last known cart, used to diff removals. */
let lastCart = null;

/** Fetches the cart as JSON. Returns null rather than throwing on failure. */
async function fetchCart() {
  try {
    const response = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Broadcasts the cart to every component here, and diffs it against the
 * previous snapshot so the undo toast knows what disappeared.
 */
async function refreshCart() {
  const cart = await fetchCart();
  if (!cart) return;

  const previous = lastCart;
  lastCart = cart;

  document.dispatchEvent(new CustomEvent('oaa:cart:refresh', { detail: { cart } }));

  if (!previous) return;

  const currentKeys = new Set(cart.items.map((item) => item.key));
  const removed = previous.items.filter((item) => !currentKeys.has(item.key));

  // Exactly one line gone, and the cart did not simply grow: treat it as a
  // removal worth offering to undo. A quantity change is not a removal.
  if (removed.length === 1 && cart.item_count < previous.item_count) {
    document.dispatchEvent(new CustomEvent('oaa:cart:removed', { detail: { line: removed[0] } }));
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // Horizon dispatches before the request and resolves this promise after its
  // section render completes. Diffing only then makes undo deterministic.
  if (event.promise) {
    event.promise.then(refreshCart).catch(() => {});
  } else {
    setTimeout(refreshCart, 120);
  }
});

// Seed the removal diff before the shopper changes a line.
void refreshCart();

/* ===========================================================================
 * <oaa-shipping-meter>
 *
 * data-threshold  free-delivery threshold in the shop's minor units
 * data-below      message template, `__AMOUNT__` replaced with the shortfall
 * data-complete   message shown once the threshold is met
 * ======================================================================== */
class OaaShippingMeter extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 0);
    this.textEl = this.querySelector('[data-meter-text]');
    this.fillEl = this.querySelector('[data-meter-fill]');
    if (!this.threshold) return;

    document.addEventListener('oaa:cart:refresh', this.#onRefresh);

    // The server rendered the correct state already; only refresh if another
    // component has since read a newer cart.
    if (lastCart) this.#render(lastCart.total_price);
  }

  disconnectedCallback() {
    document.removeEventListener('oaa:cart:refresh', this.#onRefresh);
  }

  #onRefresh = (event) => this.#render(event.detail.cart.total_price);

  #render(total) {
    const remaining = Math.max(this.threshold - total, 0);
    const progress = Math.min((total / this.threshold) * 100, 100);

    this.style.setProperty('--oaa-shipping-progress', `${progress}%`);
    this.classList.toggle('oaa-meter-shipping--complete', remaining === 0);

    if (!this.textEl) return;

    if (remaining === 0) {
      this.textEl.textContent = this.dataset.complete || '';
    } else {
      const formatted = formatMoney(remaining);
      this.textEl.innerHTML = (this.dataset.below || '').replace('__AMOUNT__', `<strong>${formatted}</strong>`);
    }
  }
}

/**
 * Formats minor units using the shop's money format. Falls back to a plain
 * decimal if the format string is unavailable, which is better than showing
 * a raw integer.
 */
function formatMoney(cents) {
  const format = window.Theme?.moneyFormat;
  const amount = (cents / 100).toFixed(2);

  if (!format) return amount;

  return format
    .replace(/\{\{\s*amount\s*\}\}/, amount)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(cents / 100)));
}

/* ===========================================================================
 * <oaa-sampler-strip>
 * Once the cap is reached the strip disappears rather than nagging.
 * ======================================================================== */
class OaaSamplerStrip extends HTMLElement {
  connectedCallback() {
    this.cap = Number(this.dataset.cap || 3);
    this.tag = this.dataset.sampleTag || 'discovery-sample';
    document.addEventListener('oaa:cart:refresh', this.#onRefresh);
    this.addEventListener('submit', this.#onSubmit);
    if (lastCart) this.#render(lastCart);
  }

  disconnectedCallback() {
    document.removeEventListener('oaa:cart:refresh', this.#onRefresh);
    this.removeEventListener('submit', this.#onSubmit);
  }

  #onRefresh = (event) => this.#render(event.detail.cart);

  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.oaa-sampler__form')) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const variantId = new FormData(form).get('id');
    const productTitle = form.closest('[data-sample-handle]')?.querySelector('.oaa-sampler__copy a')?.textContent?.trim() || '';
    const status = this.querySelector('[data-sampler-status]');
    if (!variantId) return;

    const current = lastCart || await fetchCart();
    const sampleCount = current?.items
      .filter((item) => (item.product_type || '').toLowerCase() === 'discovery sample')
      .reduce((total, item) => total + item.quantity, 0) || 0;
    if (sampleCount >= this.cap) {
      this.hidden = true;
      return;
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
      .filter(Boolean);
    const eventPromise = CartLinesUpdateEvent.createPromise();

    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: eventPromise.promise,
    }));

    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;

    try {
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Add failed');

      const cart = await fetchCart();
      if (!cart) throw new Error('Cart refresh failed');
      lastCart = cart;

      eventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'oaa-sampler-strip',
          sourceId: form.id,
          itemCount: 1,
          sections: result.sections,
          didError: false,
        },
      });
      this.#render(cart);
      if (status) status.textContent = (this.dataset.added || 'Added __TITLE__').replace('__TITLE__', productTitle);
    } catch (error) {
      eventPromise.reject(error);
      if (status) status.textContent = this.dataset.error || 'Could not add this sample.';
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };

  #render(cart) {
    const inCart = cart.items
      .filter((item) => (item.product_type || '').toLowerCase() === 'discovery sample')
      .reduce((total, item) => total + item.quantity, 0);

    this.hidden = inCart >= this.cap;

    // Hide the individual add buttons for samples already in the cart, so the
    // strip never offers something the shopper has taken.
    const handles = new Set(cart.items.map((item) => item.handle));
    this.querySelectorAll('[data-sample-handle]').forEach((node) => {
      node.toggleAttribute('data-in-cart', handles.has(node.dataset.sampleHandle));
    });
  }
}

/* ===========================================================================
 * <oaa-cart-undo>
 * A single live region holding the six-second undo offer.
 * ======================================================================== */
class OaaCartUndo extends HTMLElement {
  connectedCallback() {
    this.toast = this.querySelector('[data-undo-toast]');
    this.label = this.querySelector('[data-undo-label]');
    this.button = this.querySelector('[data-undo-action]');
    this.template = this.dataset.template || 'Removed __TITLE__';
    if (!this.toast || !this.button) return;

    document.addEventListener('oaa:cart:removed', this.#onRemoved);
    this.button.addEventListener('click', this.#onUndo);
  }

  disconnectedCallback() {
    document.removeEventListener('oaa:cart:removed', this.#onRemoved);
    clearTimeout(this.#timer);
  }

  #timer = null;
  #line = null;

  #onRemoved = (event) => {
    this.#line = event.detail.line;
    if (this.label) this.label.textContent = this.template.replace('__TITLE__', this.#line.product_title);
    this.toast.hidden = false;

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#dismiss(), 6000);
  };

  #onUndo = async () => {
    if (!this.#line) return;
    const line = this.#line;
    this.#dismiss();

    try {
      await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: line.variant_id,
              quantity: line.quantity,
              properties: line.properties || {},
              selling_plan: line.selling_plan_allocation?.selling_plan?.id,
            },
          ],
        }),
      });
      // Let Horizon re-render the cart from the source of truth.
      window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  #dismiss() {
    clearTimeout(this.#timer);
    this.#timer = null;
    this.#line = null;
    if (this.toast) this.toast.hidden = true;
  }
}

/* ===========================================================================
 * Registration
 * ======================================================================== */
const registry = {
  'oaa-shipping-meter': OaaShippingMeter,
  'oaa-sampler-strip': OaaSamplerStrip,
  'oaa-cart-undo': OaaCartUndo,
};

for (const [tag, constructor] of Object.entries(registry)) {
  if (!customElements.get(tag)) customElements.define(tag, constructor);
}
